import {Component, computed, inject, input, OnInit} from '@angular/core';
import {DatePipe, NgClass} from '@angular/common';
import {Button} from 'primeng/button';
import {Badge} from 'primeng/badge';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {
  ApprovalContext,
  FullTaDetails,
  SubmissionApprovalDetails,
  SubmissionApproversDetails
} from '../../../../api';
import {ApprovalStatusBadge, toApprovalTypeText, toInitialTypeText} from '../../../../util/app.types';
import {chainApprovals, toApprovalStatusBadge} from '../../../../util/approval-workflow.utils';
import {ManageApproversModalComponent} from '../../admin/manage-approvers-modal/manage-approvers-modal.component';
import {TaAppStore} from '../../../../state/clcta-store';
import {TaSubmissionDatasource} from '../../../../datasources/ta-submission.datasource';
import {TaSearchDatasource} from '../../../../datasources/ta-search-datasource';
import {LoadingService} from '../../../../services/loading.service';
import {PermissionService} from '../../../../services/permission.service';

/**
 * The approval slots a TA holds, one row per approval. Shared by the manage pages from approval
 * onwards: travel approved and acquittal list the travel stages, acquittal approval lists the travel
 * and acquittal stages as two tables, the cancel pages list the cancellation stage, and the
 * approval, finalise and finalised pages list the whole chain.
 */
@Component({
  selector: 'ta-approvals-table',
  imports: [
    Button,
    Badge,
    DatePipe,
    NgClass,
  ],
  providers: [
    DialogService,
  ],
  // The heading and grid stay direct children of the parent's flex column, so spacing is
  // unchanged from when this markup lived inline.
  styles: [':host { display: contents; }'],
  templateUrl: './ta-approvals-table.component.html',
})
export class TaApprovalsTableComponent implements OnInit {
  private dialogService = inject(DialogService);
  private taSubmissionDatasource = inject(TaSubmissionDatasource);
  private taSearchDatasource = inject(TaSearchDatasource);
  private loadingService = inject(LoadingService);
  private permissionService = inject(PermissionService);
  private store = inject(TaAppStore);

  taSubmission = input<FullTaDetails | undefined>();
  /** Lifecycle steps to list. An empty list means every approval the TA holds. */
  contexts = input<ApprovalContext[]>([]);
  heading = input<string>('Required Approvals');
  /** Whether the listed stages can still be reassigned on this page. */
  editable = input<boolean>(false);

  ref: DynamicDialogRef | null | undefined;

  // Only finance/admin roles get the Edit Approvers action column, on tables marked editable.
  canOverrideApprovers = computed<boolean>(() => this.permissionService.canOverrideApprovers() && this.editable());

  // Stage order then oldest first - the server returns them grouped by assignment, not by stage.
  // chainApprovals also drops the PrePayment/OverPayment rows: they record finance processing, not
  // an approver slot, and every row here offers Edit Approvers, which cannot act on them.
  approvals = computed<SubmissionApprovalDetails[]>(() => {
    const approvals = chainApprovals(this.taSubmission()?.approvals);
    const contexts = this.contexts();
    if (!contexts.length) {
      return approvals;
    }
    return approvals.filter(approval => !!approval.actionContext && contexts.includes(approval.actionContext));
  });

  ngOnInit() {
    // The Edit TA Approvers modal picks the replacement approver from the full TA user list.
    if (this.canOverrideApprovers() && this.store.userSearchOptions().length === 0) {
      this.taSearchDatasource.loadUserOptionsForSearch();
    }
  }

  statusBadge(approval: SubmissionApprovalDetails): ApprovalStatusBadge {
    return toApprovalStatusBadge(approval);
  }

  /**
   * Who the Approver/Group column names. While pending it is the assigned approver (or the group
   * that may action it); once actioned it is the user who actually actioned it, which for a group
   * slot - or an admin override - is not necessarily the assigned approver. Falls back to the
   * assignment when an actioned record carries no actioning user.
   */
  approverLabel(approval: SubmissionApprovalDetails): string {
    if (approval.status === 'Actioned' && !!approval.actionEvent?.createdByName) {
      return approval.actionEvent.createdByName;
    }
    return (approval.groupApprovers?.length ? approval.displayName : approval.approver?.name) ?? '';
  }

  approversModalConfig(): any {
    return {
      header: 'Edit TA Approvers',
      inputValues: {
        modalData: this.taSubmission(),
        availableApprovers: this.store.userSearchOptions(),
      },
      contentStyle: {
        overflow: 'auto'
      },
      modal: true,
      width: 'fit',
    };
  }

  openManageApproversModal(): void {
    this.ref = this.dialogService.open(ManageApproversModalComponent, this.approversModalConfig());
    this.ref?.onClose.subscribe((details: SubmissionApproversDetails | undefined) => {
      if (!!details) {
        this.overrideApprovers(details);
      }
    });
  }

  private overrideApprovers(details: SubmissionApproversDetails): void {
    this.loadingService.showLoading({message: 'Updating Approvers', spinnerSize: 'md'});
    // The datasource reports any failure - only a successful override changes the approval chain.
    this.taSubmissionDatasource.overrideApprovers(details).subscribe({
      next: resp => {
        this.loadingService.hideLoading();
        if (resp?.success) {
          // Reload so the approver names and status badges show the new chain.
          const submissionId = this.taSubmission()?.id;
          if (!!submissionId) {
            this.taSubmissionDatasource.loadServerTaSubmission(submissionId);
          }
        }
      },
      error: () => {
        this.loadingService.hideLoading();
      },
    });
  }

  protected readonly toInitialTypeText = toInitialTypeText;
  protected readonly toApprovalTypeText = toApprovalTypeText;
}
